import { c as _c } from "react/compiler-runtime";
// Файлы заявки: прайсы, каталоги, фото, документы, договоры. Перетаскивание
// нескольких файлов сразу, раздел подсказывается по типу и имени, картинки —
// превью сеткой, всё остальное — строкой. Байты приватные (бакет без публичных
// ссылок), поэтому превью и просмотр — через fileBlob → object URL.
import { CRM_FILE_CATEGORIES, CRM_FILE_CATEGORY_LABELS, CRM_FILE_MAX_BYTES, type CrmFile, type CrmFileCategory } from "@osmix/contracts";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Download, ExternalLink, FileImage, FileSpreadsheet, FileText, Paperclip, Trash2, Upload } from "lucide-react";
import { type DragEvent, useEffect, useId, useState } from "react";
import { useCrmFileBlob, useDeleteFile, useUpdateFile, useUploadFile } from "@/api/hooks/use-application-crm";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { errorText, fileSize, formatDayTime, guessFileCategory } from "./format";
import { Chip, downloadBlob, FieldError, Panel, Spinner, useObjectUrl } from "./ui";
type Kind = "image" | "pdf" | "text" | "other";
const kindOf = (mime: string, name: string): Kind => {
  if (mime.startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/i.test(name)) return "image";
  if (mime.includes("pdf") || /\.pdf$/i.test(name)) return "pdf";
  if (mime.startsWith("text/") || /\.(csv|txt|tsv)$/i.test(name)) return "text";
  return "other";
};
export function FileIcon(t0) {
  const $ = _c(5);
  const {
    file,
    className
  } = t0;
  const kind = kindOf(file.mime, file.filename);
  const Icon = kind === "image" ? FileImage : /\.(xlsx?|csv|tsv)$/i.test(file.filename) ? FileSpreadsheet : kind === "pdf" || kind === "text" ? FileText : Paperclip;
  let t1;
  if ($[0] !== className) {
    t1 = cn("size-4 shrink-0 text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon || $[3] !== t1) {
    t2 = <Icon className={t1} />;
    $[2] = Icon;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type Uploading = {
  key: string;
  name: string;
  error: string | null;
};
export function FilesPanel({
  applicationId,
  files,
  canManage,
  onOpen
}: {
  applicationId: string;
  files: CrmFile[];
  canManage: boolean;
  onOpen: (file: CrmFile) => void;
}) {
  const inputId = useId();
  const upload = useUploadFile(applicationId);
  const [category, setCategory] = useState<CrmFileCategory | "auto">("auto");
  const [filter, setFilter] = useState<CrmFileCategory | "all">("all");
  const [dragOver, setDragOver] = useState(false);
  const [queue, setQueue] = useState<Uploading[]>([]);
  const uploadAll = async (list: File[]) => {
    const items = list.map((file, index) => ({
      key: `${Date.now()}-${index}-${file.name}`,
      name: file.name,
      file
    }));
    setQueue(current => [...current, ...items.map(({
      key,
      name
    }) => ({
      key,
      name,
      error: null
    }))]);
    // По одному: прогресс понятнее, а шлюз не получает пачку по 50 МБ разом.
    for (const item of items) {
      try {
        if (item.file.size > CRM_FILE_MAX_BYTES) throw new Error("больше 50 МБ");
        await upload.mutateAsync({
          file: item.file,
          category: category === "auto" ? guessFileCategory(item.file) : category
        });
        setQueue(current_1 => current_1.filter(entry_0 => entry_0.key !== item.key));
      } catch (e) {
        setQueue(current_0 => current_0.map(entry => entry.key === item.key ? {
          ...entry,
          error: errorText(e)
        } : entry));
      }
    }
  };
  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault();
    setDragOver(false);
    if (!canManage) return;
    const dropped = Array.from(event.dataTransfer.files);
    if (dropped.length) void uploadAll(dropped);
  };
  const present = CRM_FILE_CATEGORIES.filter(key_0 => files.some(file_0 => file_0.category === key_0));
  const visible = filter === "all" ? files : files.filter(file_1 => file_1.category === filter);
  // Сеткой — только то, что шлюз признал картинкой (jpeg/png/webp/gif/avif): svg и прочее — строкой.
  const images = visible.filter(file_2 => file_2.isImage);
  const others = visible.filter(file_3 => !file_3.isImage);
  return <Panel title="Файлы" icon={<Paperclip className="size-4 text-muted-foreground" />} count={files.length} id="files">
      <div className="flex flex-col gap-3">
        {canManage && <div className="flex flex-col gap-2">
            <label htmlFor={inputId} onDragOver={event_0 => {
          event_0.preventDefault();
          setDragOver(true);
        }} onDragLeave={() => setDragOver(false)} onDrop={onDrop} className={cn("flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-dashed px-3 py-4 text-center text-sm transition-colors hover:bg-muted/40", dragOver && "border-primary bg-primary/5")}>
              <Upload className="size-4 text-muted-foreground" />
              <span>
                Перетащите файлы или <span className="text-primary underline-offset-4 hover:underline">выберите</span>
              </span>
              <span className="text-xs text-muted-foreground">Прайсы, каталоги, фото, документы — до 50 МБ каждый</span>
            </label>
            <input id={inputId} type="file" multiple className="sr-only" onChange={event_1 => {
          const picked = Array.from(event_1.target.files ?? []);
          event_1.target.value = "";
          if (picked.length) void uploadAll(picked);
        }} />
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="shrink-0">Раздел новых файлов</span>
              <Select aria-label="Раздел новых файлов" className="h-8 text-xs" value={category} onChange={e_0 => setCategory(e_0.target.value as CrmFileCategory | "auto")}>
                <option value="auto">Определить по файлу</option>
                {CRM_FILE_CATEGORIES.map(key_1 => <option key={key_1} value={key_1}>
                    {CRM_FILE_CATEGORY_LABELS[key_1]}
                  </option>)}
              </Select>
            </div>
          </div>}

        {queue.length > 0 && <ul className="flex flex-col gap-1 text-xs">
            {queue.map(entry_1 => <li key={entry_1.key} className="flex items-center gap-2">
                {entry_1.error ? <span className="min-w-0 flex-1 truncate text-destructive">
                    {entry_1.name}: {entry_1.error}
                  </span> : <>
                    <Spinner className="size-3.5" />
                    <span className="min-w-0 flex-1 truncate text-muted-foreground">{entry_1.name}</span>
                  </>}
                {entry_1.error && <button type="button" className="shrink-0 text-muted-foreground hover:text-foreground" onClick={() => setQueue(current_2 => current_2.filter(item_0 => item_0.key !== entry_1.key))}>
                    скрыть
                  </button>}
              </li>)}
          </ul>}

        {present.length > 1 && <div className="flex flex-wrap gap-1.5">
            <Chip active={filter === "all"} className="px-2.5 text-xs" onClick={() => setFilter("all")}>
              Все <span className="tabular-nums text-muted-foreground">{files.length}</span>
            </Chip>
            {present.map(key_2 => <Chip key={key_2} active={filter === key_2} className="px-2.5 text-xs" onClick={() => setFilter(key_2)}>
                {CRM_FILE_CATEGORY_LABELS[key_2]}
                <span className="tabular-nums text-muted-foreground">
                  {files.filter(file_4 => file_4.category === key_2).length}
                </span>
              </Chip>)}
          </div>}

        {files.length === 0 && !canManage && <p className="text-sm text-muted-foreground">Файлов пока нет</p>}

        {images.length > 0 && <div className="grid grid-cols-3 gap-2">
            {images.map(file_5 => <Thumb key={file_5.id} applicationId={applicationId} file={file_5} onOpen={() => onOpen(file_5)} />)}
          </div>}

        {others.length > 0 && <ul className="flex flex-col divide-y rounded-lg border">
            {others.map(file_6 => <li key={file_6.id} className="flex items-center gap-2 px-2.5 py-2">
                <FileIcon file={file_6} />
                <button type="button" onClick={() => onOpen(file_6)} className="min-w-0 flex-1 text-left" title={file_6.filename}>
                  <span className="block truncate text-sm hover:underline">{file_6.filename}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {CRM_FILE_CATEGORY_LABELS[file_6.category]} · {fileSize(file_6.sizeBytes)} · {formatDayTime(file_6.createdAt)}
                  </span>
                </button>
                <DownloadButton applicationId={applicationId} file={file_6} />
              </li>)}
          </ul>}
      </div>
    </Panel>;
}
function Thumb(t0) {
  const $ = _c(8);
  const {
    applicationId,
    file,
    onOpen
  } = t0;
  const {
    data: blob,
    isError
  } = useCrmFileBlob(applicationId, file.id);
  const url = useObjectUrl(blob);
  const t1 = `${file.filename} · ${CRM_FILE_CATEGORY_LABELS[file.category]}`;
  let t2;
  if ($[0] !== file.filename || $[1] !== isError || $[2] !== url) {
    t2 = url ? <img src={url} alt={file.filename} className="size-full object-cover transition-transform group-hover:scale-105" /> : isError ? <FileImage className="size-5 text-muted-foreground" /> : <Spinner className="text-muted-foreground" />;
    $[0] = file.filename;
    $[1] = isError;
    $[2] = url;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onOpen || $[5] !== t1 || $[6] !== t2) {
    t3 = <button type="button" onClick={onOpen} title={t1} className="group relative flex aspect-square items-center justify-center overflow-hidden rounded-md border bg-muted/40 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">{t2}</button>;
    $[4] = onOpen;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function DownloadButton(t0) {
  const $ = _c(17);
  const {
    applicationId,
    file
  } = t0;
  const [wanted, setWanted] = useState(false);
  const {
    data: blob,
    isFetching,
    error
  } = useCrmFileBlob(applicationId, file.id, wanted);
  let t1;
  let t2;
  if ($[0] !== blob || $[1] !== file.filename || $[2] !== wanted) {
    t1 = () => {
      if (wanted && blob) {
        downloadBlob(blob, file.filename);
        setWanted(false);
      }
    };
    t2 = [wanted, blob, file.filename];
    $[0] = blob;
    $[1] = file.filename;
    $[2] = wanted;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const t3 = `Скачать ${file.filename}`;
  let t4;
  if ($[5] !== error) {
    t4 = error ? errorText(error) : "\u0421\u043A\u0430\u0447\u0430\u0442\u044C";
    $[5] = error;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== blob || $[8] !== file.filename) {
    t5 = () => blob ? downloadBlob(blob, file.filename) : setWanted(true);
    $[7] = blob;
    $[8] = file.filename;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isFetching) {
    t6 = isFetching ? <Spinner /> : <Download className="size-4" />;
    $[10] = isFetching;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <Button size="icon-sm" variant="ghost" aria-label={t3} title={t4} onClick={t5}>{t6}</Button>;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/**
 * Просмотр файла с управлением: PDF — во встроенном просмотрщике, картинка —
 * целиком, CSV и текст — первыми строками; скачать, открыть во вкладке,
 * переименовать, сменить раздел, удалить.
 */
export function FileDialog(t0) {
  const $ = _c(90);
  const {
    applicationId,
    file,
    canManage,
    onClose
  } = t0;
  const {
    data: blob,
    error
  } = useCrmFileBlob(applicationId, file?.id ?? "", !!file);
  const url = useObjectUrl(blob);
  const update = useUpdateFile(applicationId);
  const remove = useDeleteFile(applicationId);
  const [name, setName] = useState(file?.filename ?? "");
  const [text, setText] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [actionError, setActionError] = useState(null);
  let t1;
  if ($[0] !== blob?.type || $[1] !== file) {
    t1 = file ? kindOf(blob?.type || file.mime, file.filename) : "other";
    $[0] = blob?.type;
    $[1] = file;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const kind = t1;
  let t2;
  if ($[3] !== file?.filename) {
    t2 = () => {
      setName(file?.filename ?? "");
      setActionError(null);
    };
    $[3] = file?.filename;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = file?.id;
  const t4 = file?.filename;
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = [t3, t4];
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  useEffect(t2, t5);
  let t6;
  let t7;
  if ($[8] !== blob || $[9] !== kind) {
    t6 = () => {
      setText(null);
      if (!blob || kind !== "text") {
        return;
      }
      let alive = true;
      blob.text().then(value => {
        if (alive) {
          setText(value.split(/\r?\n/).slice(0, 200).join("\n"));
        }
      });
      return () => {
        alive = false;
      };
    };
    t7 = [blob, kind];
    $[8] = blob;
    $[9] = kind;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  if (!file) {
    return null;
  }
  let t8;
  if ($[12] !== file.filename || $[13] !== file.id || $[14] !== name || $[15] !== update) {
    t8 = async () => {
      const next = name.trim();
      if (!next || next === file.filename) {
        return;
      }
      setActionError(null);
      ;
      try {
        await update.mutateAsync({
          fileId: file.id,
          patch: {
            filename: next
          }
        });
      } catch (t9) {
        const e = t9;
        setActionError(errorText(e));
      }
    };
    $[12] = file.filename;
    $[13] = file.id;
    $[14] = name;
    $[15] = update;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const rename = t8;
  let t9;
  if ($[17] !== onClose) {
    t9 = open => !open && onClose();
    $[17] = onClose;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== file.filename) {
    t10 = <DialogTitle className="truncate pr-8">{file.filename}</DialogTitle>;
    $[19] = file.filename;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = CRM_FILE_CATEGORY_LABELS[file.category];
  let t12;
  if ($[21] !== file.sizeBytes) {
    t12 = fileSize(file.sizeBytes);
    $[21] = file.sizeBytes;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== file.createdAt) {
    t13 = formatDayTime(file.createdAt);
    $[23] = file.createdAt;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = file.uploadedBy && ` · ${file.uploadedBy.name}`;
  let t15;
  if ($[25] !== t11 || $[26] !== t12 || $[27] !== t13 || $[28] !== t14) {
    t15 = <p className="text-xs text-muted-foreground">{t11} · {t12} · {t13}{t14}</p>;
    $[25] = t11;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t10 || $[31] !== t15) {
    t16 = <DialogHeader>{t10}{t15}</DialogHeader>;
    $[30] = t10;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== error || $[34] !== file.filename || $[35] !== kind || $[36] !== text || $[37] !== url) {
    t17 = <div className="flex min-h-[40dvh] flex-1 items-center justify-center overflow-auto rounded-md border bg-muted/30">{error ? <p className="p-6 text-sm text-destructive">Не удалось загрузить файл: {errorText(error)}</p> : !url ? <span className="inline-flex items-center gap-2 p-6 text-sm text-muted-foreground"><Spinner /> Загружаем файл…</span> : kind === "pdf" ? <iframe src={url} title={file.filename} className="h-[60dvh] w-full" /> : kind === "image" ? <img src={url} alt={file.filename} className="max-h-[60dvh] w-auto object-contain" /> : kind === "text" && text !== null ? <pre className="max-h-[60dvh] w-full overflow-auto p-4 text-xs whitespace-pre">{text}</pre> : <p className="p-6 text-center text-sm text-muted-foreground">Предпросмотр для этого формата недоступен — скачайте файл.</p>}</div>;
    $[33] = error;
    $[34] = file.filename;
    $[35] = kind;
    $[36] = text;
    $[37] = url;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== canManage || $[40] !== file.category || $[41] !== file.filename || $[42] !== file.id || $[43] !== name || $[44] !== rename || $[45] !== update) {
    t18 = canManage && <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_12rem]"><form className="flex min-w-0 gap-2" onSubmit={event => {
        event.preventDefault();
        rename();
      }}><Input aria-label={"\u0418\u043C\u044F \u0444\u0430\u0439\u043B\u0430"} value={name} className="h-8" onChange={e_0 => setName(e_0.target.value)} /><Button type="submit" size="sm" variant="outline" disabled={update.isPending || !name.trim() || name.trim() === file.filename}>Переименовать</Button></form><Select aria-label={"\u0420\u0430\u0437\u0434\u0435\u043B \u0444\u0430\u0439\u043B\u0430"} className="h-8" value={file.category} disabled={update.isPending} onChange={e_1 => {
        setActionError(null);
        update.mutateAsync({
          fileId: file.id,
          patch: {
            category: e_1.target.value as CrmFileCategory
          }
        }).catch(err => setActionError(errorText(err)));
      }}>{CRM_FILE_CATEGORIES.map(_temp)}</Select></div>;
    $[39] = canManage;
    $[40] = file.category;
    $[41] = file.filename;
    $[42] = file.id;
    $[43] = name;
    $[44] = rename;
    $[45] = update;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== actionError) {
    t19 = <FieldError>{actionError}</FieldError>;
    $[47] = actionError;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== canManage) {
    t20 = canManage ? <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}><Trash2 className="size-4" />Удалить</Button> : <span />;
    $[49] = canManage;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  const t21 = !url;
  let t22;
  if ($[51] !== url) {
    t22 = () => url && window.open(url, "_blank", "noopener");
    $[51] = url;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <ExternalLink className="size-4" />;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t21 || $[55] !== t22) {
    t24 = <Button variant="outline" disabled={t21} onClick={t22}>{t23}Открыть во вкладке</Button>;
    $[54] = t21;
    $[55] = t22;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  const t25 = !blob;
  let t26;
  if ($[57] !== blob || $[58] !== file.filename) {
    t26 = () => blob && downloadBlob(blob, file.filename);
    $[57] = blob;
    $[58] = file.filename;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Download className="size-4" />;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t25 || $[62] !== t26) {
    t28 = <Button disabled={t25} onClick={t26}>{t27}Скачать</Button>;
    $[61] = t25;
    $[62] = t26;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t24 || $[65] !== t28) {
    t29 = <div className="flex flex-wrap gap-2">{t24}{t28}</div>;
    $[64] = t24;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t20 || $[68] !== t29) {
    t30 = <div className="flex flex-wrap justify-between gap-2">{t20}{t29}</div>;
    $[67] = t20;
    $[68] = t29;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  const t31 = `«${file.filename}» пропадёт из карточки и из касаний, к которым приложен.`;
  let t32;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = () => setConfirmDelete(false);
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== file.id || $[72] !== onClose || $[73] !== remove) {
    t33 = () => remove.mutate(file.id, {
      onSuccess: () => {
        setConfirmDelete(false);
        onClose();
      },
      onError: e_2 => {
        setConfirmDelete(false);
        setActionError(errorText(e_2));
      }
    });
    $[71] = file.id;
    $[72] = onClose;
    $[73] = remove;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== confirmDelete || $[76] !== remove.isPending || $[77] !== t31 || $[78] !== t33) {
    t34 = <ConfirmDialog open={confirmDelete} title={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0444\u0430\u0439\u043B?"} description={t31} confirmLabel={"\u0423\u0434\u0430\u043B\u0438\u0442\u044C"} confirmVariant="destructive" busy={remove.isPending} onClose={t32} onConfirm={t33} />;
    $[75] = confirmDelete;
    $[76] = remove.isPending;
    $[77] = t31;
    $[78] = t33;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== t16 || $[81] !== t17 || $[82] !== t18 || $[83] !== t19 || $[84] !== t30 || $[85] !== t34) {
    t35 = <DialogContent className="flex max-h-[92dvh] flex-col gap-3 sm:max-w-3xl">{t16}{t17}{t18}{t19}{t30}{t34}</DialogContent>;
    $[80] = t16;
    $[81] = t17;
    $[82] = t18;
    $[83] = t19;
    $[84] = t30;
    $[85] = t34;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== t35 || $[88] !== t9) {
    t36 = <Dialog open={true} onOpenChange={t9}>{t35}</Dialog>;
    $[87] = t35;
    $[88] = t9;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  return t36;
}
function _temp(key) {
  return <option key={key} value={key}>{CRM_FILE_CATEGORY_LABELS[key]}</option>;
}